import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import Icon from 'components/Utils/NewIcon/index';
import PerformanceUtils from 'services/PerformanceUtils';
import CalendarUtils from 'services/CalendarUtils';
import DefaultModal from 'components/Utils/DefaultModal';
import './style.scss';
import StaffUtils from 'services/StaffUtils';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';

const CPPTimelineCall = ({ call, showUser = true }) => {
  const { type, logs } = call;

  const dispatch = useAppDispatch();
  const ownId = useAppSelector((state) => state.authentication.activeUserId);
  const admins = useAppSelector((state) => state.admins.byId);
  const physicians = useAppSelector((state) => state.physicians.byId);

  const staff = physicians[call.physician_id] || admins[call.physician_id];

  const [openModal, setOpenModal] = useState(false);

  const icon = call.type === 'phone' ? 'phone' : 'video';
  const savingVoicemail = useIsLoading(api.Resource.submitCallLog);

  async function onWentToVoicemail() {
    let log = logs.find((log) => log.status === 'answered' || log.status === 'connected');
    if (!log) {
      log = logs[logs.length - 1];
    }
    if (log) {
      await dispatch(
        api.Resource.submitCallLog({
          callLog: { ...log, status: 'voicemail', date: log.date + 1 },
        }),
      );
    }
  }

  const { title, durationString, startDate } = useMemo(() => {
    const { type, date, date_end } = call;

    let title = `${PerformanceUtils.capitalizeFirstLetter(type)} Call`;
    if (showUser) {
      title = StaffUtils.getName(staff);
    }

    const duration = moment.duration(moment.unix(date_end).diff(moment.unix(date)));
    let durationSeconds = duration.asSeconds() % 60;
    let durationMinutes = Math.floor(duration.asMinutes());

    let durationString;

    if (!isNaN(durationSeconds)) {
      durationString = `${durationSeconds}s`;
      if (durationMinutes > 0) durationString = `${durationMinutes}m ${durationString}`;
      durationString = `(${durationString})`;
    }

    const startDate = CalendarUtils.format(date, 'h:mm A');

    return { title, durationString, startDate };
  }, [staff, showUser, call]);

  const { description, wentToVoicemail } = useMemo(() => {
    const pickedUp = logs.find((log) => log.status === 'connected' || log.status === 'answered');
    const wentToVoicemail = logs.find((log) => log.status === 'voicemail');
    const declined = logs.find((log) => log.status === 'declined');
    const description = wentToVoicemail
      ? 'Voicemail'
      : pickedUp
      ? 'Answered'
      : declined
      ? 'Declined'
      : 'Did not answer';

    return { description, wentToVoicemail };
  }, [logs]);

  const isOwner = call.physician_id === ownId;
  const canMarkVoicemail = !wentToVoicemail && isOwner && type === 'phone';

  return (
    <>
      <div className="item-wrapper clickable" onClick={() => setOpenModal(true)}>
        <div className="cpp-call clickable">
          <div className="top-bar">
            <Icon src={icon} />
            <span className="small-title">{title}</span>
          </div>
          <div className="item-content">
            <span style={{ display: 'block' }}>
              {startDate} {durationString}
            </span>
            <span style={{ display: 'block' }}>{description}</span>
          </div>
        </div>
      </div>
      {openModal && (
        <DefaultModal
          className="modal call-modal"
          title={`${title} (${startDate})`}
          wrap={true}
          padding={true}
          content={
            <>
              <table className="call-log mb-5">
                <thead>
                  <tr className="log row">
                    <td>Time</td>
                    <td>Role</td>
                    <td>Status</td>
                    <td>Device</td>
                  </tr>
                </thead>
                <tbody>
                  {(logs || []).map((log, index) => (
                    <tr className="log row" key={index}>
                      <td>{CalendarUtils.format(log.date, 'h:mm:ss A')}</td>
                      <td>
                        {PerformanceUtils.capitalizeFirstLetter(
                          log.role === 'physician' ? 'practitioner' : log.role,
                        )}
                      </td>
                      <td>{PerformanceUtils.capitalizeFirstLetter(log.status)}</td>
                      <td>{log.device}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {canMarkVoicemail && (
                <DefaultButton
                  icon="voicemail"
                  className="block white"
                  name="Mark as 'Went to Voicemail'"
                  onClick={onWentToVoicemail}
                  style={{ width: '100%' }}
                  loading={savingVoicemail}
                />
              )}
            </>
          }
          isOpen={openModal}
          onRequestClose={() => setOpenModal(false)}
        />
      )}
    </>
  );
};

CPPTimelineCall.propTypes = {
  call: PropTypes.object.isRequired,
};

export default CPPTimelineCall;
